import { DIGEST_LIST_CAP } from "./outcomeDigest";
import { SCENARIO_MAP, type Scenario } from "./scenario";
import { DAY_MS } from "./window";

/**
 * Agent Brains 13.4, verbatim: "When customers repeatedly hit no-answer on the
 * same topic, that becomes a ranked owner question in the weekly digest: '7
 * people asked about lab-grown trade-ins this month, what's your policy?' The
 * KB grows by interrogation exactly where real demand is, not by anyone
 * guessing what to document."
 *
 * A gap ticket today is a draft the reply path flagged `needs_owner_knowledge`
 * whose question the KB still cannot answer (packages/db/src/schema.ts, A3.6):
 * the KB had no answer, so the shop sent nothing it could not stand behind.
 * The draft may still wait in the queue or may have left it (`released`,
 * TA-1361, below); either way the fact is missing. This module is the ranking
 * half of that sentence, in the split `buildOutcomeDigest` set: the loader in
 * packages/db narrows rows and files each question under a topic, and this
 * decides the order, the exclusions and the counts, with no IO and no clock
 * of its own.
 *
 * WHAT A ROW SAYS. A topic, how many distinct threads and distinct contacts
 * raised it, and how long the oldest open ticket on it has waited. Threads
 * rank first because a customer who asks three times is one conversation
 * still waiting, not three customers (the owner interview agenda ranks by
 * distinct threads for the same reason, so the two lists cannot rank one shop
 * two ways). Contacts break a tie because "7 people" is the figure 13.4 puts
 * to the owner. The topic's name breaks the last tie, so the order never
 * depends on the order a caller happened to read rows in.
 *
 * A TOPIC IS A NAME, NEVER A QUOTE. It is the stage 4 KB topic the question's
 * words match (`kbTopicsOf`, resolved by the loader), else the scenario the
 * router recorded, spelled as the interview agenda spells it. Nothing the
 * customer typed and nothing about who they are reaches this function's
 * output (CLAUDE.md section 10): the output is built field by field, so a
 * ticket that carries more than its type says cannot leak the extra.
 *
 * CLASS D IS NEVER A KB QUESTION. Complaints, fraud suspects and unknowns
 * (Agent Brains section 9, T3 in CLAUDE.md section 3) are not KB questions, and a
 * ranked list that asked the owner for a "policy" on a stone that fell out
 * would turn his grievance into a documentation chore. They are left out of
 * the ranking and counted, so the owner is told they are being held and the
 * digest does not read as if nothing were: complaints and fraud suspects in
 * one line, the router's unknown row in its own (below). The class is read off
 * `SCENARIO_MAP`, never restated here.
 *
 * THE ROUTER'S UNKNOWN ROW IS COUNTED APART. `unknown` is class D, but it is
 * the row the router files a question under when it matched nothing, and a
 * probe of ten invented questions sent six ordinary ones (hours, shipping,
 * financing, a vintage brooch, a price, a ring that fell apart) there. Calling
 * those threads "held for a human" claims a person is looking at them, when
 * what happened is that the router could not place the question. So they are
 * counted in their own line, "N threads the router could not place"
 * (`routerUnknownLine`), and never in the held line. They are still never
 * ranked: the row is T3 and stays exactly as `SCENARIO_MAP` has it, and the
 * count is a reading of the row, not an edit of it. RATIFIED by Kabir
 * 2026-10-02 (decision list item 126, typed reply "Yes I want to continue the
 * production" to the accept-the-recs question), which accepted the
 * recommendation to count apart over keeping the row in the held line or
 * widening the router rows.
 *
 * A DAMAGE REPORT ON THE UNKNOWN ROW STAYS HELD. TA-464 leaves "it arrived
 * cracked" on `unknown` when its thread has no class D row, on purpose, and
 * decision list item 94(a) gives it the reply path's fixed line, so a person
 * does hold it and the router did not simply fail. The loader reads the
 * question once, as it does for the topic, and keeps only whether it reports
 * damage (`GapTicket.damageReport`, the reply path's own `reportsDamage`).
 * Such a ticket keeps the line it had before item 126, the held one, and is
 * not counted as unplaced by the router. The sheet's example list for item 126
 * names "my ring fell apart" among the six, which reads the other way; the
 * conservative reading was taken and the call is Kabir's (FINDINGS, DG).
 *
 * NOTHING IS DROPPED QUIETLY. A ticket with no recorded route cannot be shown
 * not to be class D, so it is not listed; and the vendor row, whose class
 * cell is "n/a" because it sends nothing, is not class D and is not a KB
 * question either. Both are counted as `unplaced`.
 *
 * THE CAP is `DIGEST_LIST_CAP`, the one reading bound the digest already has,
 * and like it the cap hides rows and never figures: the totals cover every
 * topic and `overflow` says how many the list did not show.
 *
 * A RELEASED TICKET IS ITS OWN GROUP (TA-1361). A ticket whose draft left the
 * queue (the holding line approved, sent or transmitted, or superseded by the
 * owner's own reply in the Meta inbox) is still an open KB question: the
 * customer heard "let me check on that" and the KB never learned the fact
 * (CLAUDE.md section 4, the no-answer rule; section 0). It is ranked the same
 * way into `released`, never into a figure the queue is counted by: the list,
 * its totals and the held, router and unplaced lines read exactly as they did,
 * so "needs you" does not move, and `knowledgeGapsClear` stays the queue's own
 * question. A released class D ticket is not in the group: it was never a KB
 * question and a person already handled it. A released ticket on the router's
 * unknown row or with no route is counted in the group's `unplaced`, not
 * listed, for the reasons above.
 */

export interface GapTicket {
  readonly draftId: string;
  readonly threadId: string;
  readonly contactId: string;
  /**
   * The scenario the router recorded for the question this draft answers, or
   * null when no routing record could be found for it.
   */
  readonly scenario: Scenario | null;
  /**
   * The stage 4 KB topic the question's words match, or null when none does.
   * One topic per ticket: the most specific, which is the first `kbTopicsOf`
   * returns. Resolved by the loader, which is the only code that reads the
   * question, so this module never sees it.
   */
  readonly kbTopic: string | null;
  /**
   * Whether the question reports damage, by the reply path's own
   * `reportsDamage` (decision list item 94a). Read by the loader, which is the
   * only code that reads the question, and only this boolean is kept. It
   * matters on one row: a damage report the router left on `unknown` is held
   * by a person, so it stays in the held line rather than the router's. False
   * when the question is gone.
   */
  readonly damageReport: boolean;
  /** When the ticket was raised: when its draft was written. */
  readonly openedAt: Date;
  /**
   * TA-1195. The DRAFT kb version holding the owner's answer to this gap, when `pnpm artifacts kb answer-gap`
   * has written one that is not live yet. Absent or null: no answer is drafted. A ticket with a drafted answer
   * is still open (the fact is not in the KB customers are answered from until a person approves the version),
   * so it is still ranked, and the digest says the answer is waiting on approval. Once that version is live the
   * loader no longer returns the ticket at all.
   */
  readonly answerDraftedVersion?: number | null;
  /**
   * TA-1361. True when the ticket's draft has left the queue (approved, sent, transmitted, or superseded by the
   * owner's own reply) while the KB still lacks the fact; false while it waits as `pending_approval` or
   * `blocked`, which is what "needs you" counts. A released ticket is ranked into its own group, never into the
   * queue's figures.
   */
  readonly released: boolean;
}

export type GapTopicSource = "kb-topic" | "scenario";

export interface KnowledgeGapTopic {
  /** A stage 4 topic name, or a scenario name with hyphens, as the interview agenda files it. */
  readonly topic: string;
  readonly source: GapTopicSource;
  /** Distinct threads with an open ticket on the topic. What the ranking is on. */
  readonly threads: number;
  /** Distinct contacts behind those threads. */
  readonly contacts: number;
  /** Open tickets on the topic. Reported, never ranked on. */
  readonly tickets: number;
  readonly oldestOpenAt: Date;
  /** Whole days, floored so a wait is never overstated. */
  readonly oldestOpenDays: number;
  /**
   * TA-1195. How many of the open tickets have an owner answer drafted and awaiting approval. Present only
   * when above zero, so a topic with none reads exactly as it did before.
   */
  readonly answerDrafted?: number;
}

export interface KnowledgeGaps {
  /** Most asked first, capped at the cap. */
  readonly topics: readonly KnowledgeGapTopic[];
  /** Every topic with an open ticket. Never capped. */
  readonly topicsTotal: number;
  /** Topics the cap hid. Zero on a list that fit. */
  readonly overflow: number;
  /** Distinct threads across every ranked topic, hidden ones included. */
  readonly threads: number;
  /** Distinct contacts across every ranked topic, hidden ones included. */
  readonly contacts: number;
  /**
   * Distinct threads held for a human because their route is class D, the
   * router's unknown row apart (it is `routerUnknown`) unless its question
   * reports damage. Never listed.
   */
  readonly heldForHuman: number;
  /**
   * Distinct threads with an open ticket on the router's `unknown` row whose
   * question does not report damage: the router could not place it. Counted
   * apart from `heldForHuman` (decision list item 126) and never listed or
   * ranked.
   */
  readonly routerUnknown: number;
  /** Distinct threads with a ticket whose route is missing or has no class. Never listed. */
  readonly unplaced: number;
  /**
   * TA-1361. The released tickets: answered with a holding line, the fact still missing. Present only when there
   * is one to say, so a section with none reads exactly as it did before. None of its figures is in any figure
   * above.
   */
  readonly released?: ReleasedKnowledgeGaps;
}

/** TA-1361. The released group, ranked and capped like the list. Its figures never join the queue's. */
export interface ReleasedKnowledgeGaps {
  /** Most asked first, capped at the cap. */
  readonly topics: readonly KnowledgeGapTopic[];
  /** Every topic with a released ticket. Never capped. */
  readonly topicsTotal: number;
  /** Topics the cap hid. */
  readonly overflow: number;
  /** Distinct threads across every topic in the group, hidden ones included. */
  readonly threads: number;
  /** Distinct contacts across every topic in the group, hidden ones included. */
  readonly contacts: number;
  /** Distinct threads with a released ticket on the router's unknown row or with no route. Never listed. */
  readonly unplaced: number;
}

export interface KnowledgeGapsInput {
  readonly tickets: readonly GapTicket[];
  readonly now: Date;
  readonly cap?: number;
}

/**
 * The row the router files a question under when it matched nothing. Named
 * here only to count it apart; the row itself, and its class, stay in
 * `SCENARIO_MAP`.
 */
const ROUTER_UNKNOWN: Scenario = "unknown";

/** The slug the owner interview agenda gives a scenario: the `## <topic>` name. */
const scenarioSlug = (s: Scenario): string => s.replace(/_/g, "-");

/**
 * Code-unit order rather than `localeCompare`: a topic is an ASCII slug and
 * this is the last tiebreak, so it must not move with the process's locale or
 * the ICU build that happens to be installed.
 */
const byName = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

interface Tally {
  readonly topic: string;
  readonly source: GapTopicSource;
  readonly threads: Set<string>;
  readonly contacts: Set<string>;
  readonly drafts: Set<string>;
  readonly drafted: Set<string>;
  oldestOpenAt: Date;
}

/**
 * Where one ticket is filed: the single reading `rankKnowledgeGaps` ranks by and `pnpm artifacts kb answer-gap`
 * resolves a topic name by, so the two can never disagree about which tickets a topic holds (TA-1195). Only
 * `topic` is an owner-answerable KB question; the others are counted, never listed.
 */
export type GapFiling =
  | { readonly kind: "topic"; readonly topic: string; readonly source: GapTopicSource }
  | { readonly kind: "router-unknown" }
  | { readonly kind: "held" }
  | { readonly kind: "unplaced" };

export function gapFiling(t: GapTicket): GapFiling {
  if (t.scenario === ROUTER_UNKNOWN && !t.damageReport) return { kind: "router-unknown" };
  const klass = t.scenario === null ? null : SCENARIO_MAP[t.scenario].graduationClass;
  if (klass === "D") return { kind: "held" };
  if (t.scenario === null || klass === null || klass === "n/a") return { kind: "unplaced" };
  return t.kbTopic === null
    ? { kind: "topic", topic: scenarioSlug(t.scenario), source: "scenario" }
    : { kind: "topic", topic: t.kbTopic, source: "kb-topic" };
}

/** The topics one group of tickets is ranked into: the queue's list, or the released group (TA-1361). */
interface TopicTallies {
  readonly tallies: Map<string, Tally>;
  readonly threads: Set<string>;
  readonly contacts: Set<string>;
}

const newTopicTallies = (): TopicTallies => ({ tallies: new Map(), threads: new Set(), contacts: new Set() });

function tallyTopic(into: TopicTallies, t: GapTicket, topic: string, source: GapTopicSource): void {
  // Keyed by source as well, so a KB topic that one day shares a scenario's
  // slug is two rows rather than one row that merges two kinds of subject.
  const key = `${source}:${topic}`;
  let tally = into.tallies.get(key);
  if (tally === undefined) {
    tally = { topic, source, threads: new Set(), contacts: new Set(), drafts: new Set(), drafted: new Set(), oldestOpenAt: t.openedAt };
    into.tallies.set(key, tally);
  }
  if (t.answerDraftedVersion !== undefined && t.answerDraftedVersion !== null) tally.drafted.add(t.draftId);
  tally.threads.add(t.threadId);
  tally.contacts.add(t.contactId);
  tally.drafts.add(t.draftId);
  if (t.openedAt.getTime() < tally.oldestOpenAt.getTime()) tally.oldestOpenAt = t.openedAt;
  into.threads.add(t.threadId);
  into.contacts.add(t.contactId);
}

function rankTopics(
  from: TopicTallies,
  now: Date,
  cap: number,
): Pick<KnowledgeGaps, "topics" | "topicsTotal" | "overflow" | "threads" | "contacts"> {
  const ordered = [...from.tallies.values()].sort(
    (a, b) =>
      b.threads.size - a.threads.size ||
      b.contacts.size - a.contacts.size ||
      byName(a.topic, b.topic) ||
      byName(a.source, b.source),
  );

  const topics: KnowledgeGapTopic[] = ordered.slice(0, cap).map((t) => ({
    topic: t.topic,
    source: t.source,
    threads: t.threads.size,
    contacts: t.contacts.size,
    tickets: t.drafts.size,
    oldestOpenAt: t.oldestOpenAt,
    oldestOpenDays: Math.max(0, Math.floor((now.getTime() - t.oldestOpenAt.getTime()) / DAY_MS)),
    ...(t.drafted.size > 0 ? { answerDrafted: t.drafted.size } : {}),
  }));

  return {
    topics,
    topicsTotal: ordered.length,
    overflow: Math.max(0, ordered.length - cap),
    threads: from.threads.size,
    contacts: from.contacts.size,
  };
}

export function rankKnowledgeGaps(input: KnowledgeGapsInput): KnowledgeGaps {
  const cap = input.cap ?? DIGEST_LIST_CAP;

  const queued = newTopicTallies();
  const held = new Set<string>();
  const routerUnknown = new Set<string>();
  const unplaced = new Set<string>();
  // TA-1361: the released tickets, ranked apart so no queue figure moves.
  const released = newTopicTallies();
  const releasedUnplaced = new Set<string>();
  // A row read twice, from a join that fans out, is one ticket.
  const seen = new Set<string>();

  for (const t of input.tickets) {
    if (seen.has(t.draftId)) continue;
    seen.add(t.draftId);

    // The filing is read once, by `gapFiling`, in the order this loop always applied. The router's unknown
    // row first, because it IS class D and would otherwise land in the held line (decision list item 126),
    // except a damage report, which a person holds (TA-464, item 94a). Then class D. Then a ticket with no
    // route or no class, which is not shown to be a KB question and is counted as unplaced, not listed.
    const filing = gapFiling(t);

    // TA-1361: a released ticket on a KB topic joins the released group; one the router could not place or
    // with no route is counted there, not listed; a released class D ticket was handled by a person and was
    // never a KB question, so it is in neither.
    if (t.released) {
      if (filing.kind === "topic") tallyTopic(released, t, filing.topic, filing.source);
      else if (filing.kind === "router-unknown" || filing.kind === "unplaced") releasedUnplaced.add(t.threadId);
      continue;
    }

    if (filing.kind === "router-unknown") {
      routerUnknown.add(t.threadId);
      continue;
    }
    if (filing.kind === "held") {
      held.add(t.threadId);
      continue;
    }
    if (filing.kind === "unplaced") {
      unplaced.add(t.threadId);
      continue;
    }
    tallyTopic(queued, t, filing.topic, filing.source);
  }

  const releasedGroup = rankTopics(released, input.now, cap);
  return {
    ...rankTopics(queued, input.now, cap),
    heldForHuman: held.size,
    routerUnknown: routerUnknown.size,
    unplaced: unplaced.size,
    // Present only when there is one to say, so a section with none is exactly the object it was before TA-1361.
    ...(releasedGroup.topicsTotal > 0 || releasedUnplaced.size > 0
      ? { released: { ...releasedGroup, unplaced: releasedUnplaced.size } }
      : {}),
  };
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);
const count = (n: number, one: string, many: string): string => `${n} ${plural(n, one, many)}`;

/** The section's name, in the vault's words, so every surface titles it alike. */
export const KNOWLEDGE_GAPS_TITLE = "questions the shop could not answer";

/**
 * The one line that tells the owner what the list left out on purpose. A
 * sentence rather than a number so the worker, the terminal and the page say
 * the same thing in the same words. Callers print it when the count is above
 * zero.
 */
export function heldForHumanLine(n: number): string {
  return `${n} class D ${plural(n, "thread", "threads")} held for a human, not ${plural(n, "a KB gap", "KB gaps")}`;
}

/**
 * The router's unknown row, counted apart from the class D line (decision list
 * item 126). The item's own words: "N threads the router could not place".
 * The line says what happened, not what was done about it, because nothing is
 * claimed about a person holding these. Callers print it when the count is
 * above zero.
 */
export function routerUnknownLine(n: number): string {
  return `${count(n, "thread", "threads")} the router could not place`;
}

/**
 * The same, for tickets the section could not file: no route on record, or a
 * row with no class. They are counted so the owner is not shown a total that
 * quietly leaves them out, and they are not listed because nothing says what
 * they were about.
 */
export function unplacedLine(n: number): string {
  return `${count(n, "thread", "threads")} with an open ticket that could not be placed under a topic, not listed`;
}

/** A topic's row after its name, so a page can set the name apart and say the rest in the terminal's words. */
export function knowledgeGapFigures(t: KnowledgeGapTopic): string {
  const base =
    `${count(t.threads, "thread", "threads")}, ${count(t.contacts, "contact", "contacts")}, ` +
    `oldest open ${count(t.oldestOpenDays, "day", "days")}`;
  // TA-1195: the owner has answered and the answer is a draft. The topic is still open until a person approves it.
  if (t.answerDrafted === undefined || t.answerDrafted === 0) return base;
  return t.answerDrafted >= t.tickets
    ? `${base}, answer drafted, awaiting approval`
    : `${base}, answer drafted for ${t.answerDrafted} of ${t.tickets}, awaiting approval`;
}

/**
 * True only when there is nothing to say: no topic is open, no thread is held
 * for a human, no thread is one the router could not place and no ticket is
 * unplaced. Every surface that wants to tell the
 * owner "nothing is waiting" asks this, so none can say it above a line that
 * says otherwise: an unplaced or held ticket is still an open draft the owner
 * has to answer, only one this section cannot rank.
 *
 * TA-1361: it is the queue's question, so the released group does not make it
 * false. A surface that says "nothing is waiting" with a released group beside
 * it says nothing is waiting ON HIM, and shows the group (`knowledgeGapLines`).
 */
export function knowledgeGapsClear(g: KnowledgeGaps): boolean {
  return g.topicsTotal === 0 && g.heldForHuman === 0 && g.routerUnknown === 0 && g.unplaced === 0;
}

/**
 * TA-1361. The released group's name, in plain words: what happened to the customer, and what is still missing.
 * Every surface titles the group with it.
 */
export const RELEASED_GAPS_TITLE = "answered with a holding line, the fact is still missing";

/** TA-1361: the released tickets the group could not file under a topic. Callers print it when above zero. */
export function releasedUnplacedLine(n: number): string {
  return `${count(n, "thread", "threads")} answered with a holding line that could not be placed under a topic, not listed`;
}

/** What the cap hid, in a sentence, or null when it hid nothing. The list's or the released group's. */
export function knowledgeGapOverflowLine(g: Pick<KnowledgeGaps, "overflow" | "topics" | "topicsTotal">): string | null {
  if (g.overflow === 0) return null;
  return (
    `${g.overflow} more ${plural(g.overflow, "topic", "topics")} not listed: ` +
    `the list stops at the ${g.topics.length} most asked, ${g.topicsTotal} topics in all`
  );
}

/**
 * The section's headline. "None open" only when nothing is open, held,
 * unknown to the router or unplaced. With no topic to rank but a held or unplaced ticket, it says there
 * is no topic to list, and the lines under it say what is held. With the queue clear but a released group
 * beside it, "none waiting on you" (TA-1361): the released questions are still open, so "none open" would be false.
 */
function headline(g: KnowledgeGaps): string {
  if (g.topicsTotal > 0) {
    return (
      `${KNOWLEDGE_GAPS_TITLE}: ${count(g.topicsTotal, "topic", "topics")}, ` +
      `${count(g.threads, "thread", "threads")}, ${count(g.contacts, "contact", "contacts")}`
    );
  }
  if (!knowledgeGapsClear(g)) return `${KNOWLEDGE_GAPS_TITLE}: no topic to list`;
  return `${KNOWLEDGE_GAPS_TITLE}: ${g.released === undefined ? "none open" : "none waiting on you"}`;
}

/** TA-1361: the released group as lines, after the list's, or none when there is no group. */
function releasedLines(g: KnowledgeGaps): string[] {
  const r = g.released;
  if (r === undefined) return [];
  const lines: string[] = [
    `${RELEASED_GAPS_TITLE}: ${count(r.topicsTotal, "topic", "topics")}, ` +
      `${count(r.threads, "thread", "threads")}, ${count(r.contacts, "contact", "contacts")}`,
  ];
  for (const t of r.topics) lines.push(`  ${t.topic}: ${knowledgeGapFigures(t)}`);
  const overflow = knowledgeGapOverflowLine(r);
  if (overflow !== null) lines.push(`  ${overflow}`);
  if (r.unplaced > 0) lines.push(`  ${releasedUnplacedLine(r.unplaced)}`);
  return lines;
}

/**
 * The section as plain lines, for the terminal. Callers indent them. Counts
 * and topic names only, and the cap's overflow and every exclusion are said
 * whenever they are above zero, so no surface can print the list without the
 * numbers that keep it honest.
 */
export function knowledgeGapLines(g: KnowledgeGaps): string[] {
  const lines: string[] = [headline(g)];
  for (const t of g.topics) lines.push(`  ${t.topic}: ${knowledgeGapFigures(t)}`);
  const overflow = knowledgeGapOverflowLine(g);
  if (overflow !== null) lines.push(`  ${overflow}`);
  if (g.heldForHuman > 0) lines.push(`  ${heldForHumanLine(g.heldForHuman)}`);
  if (g.routerUnknown > 0) lines.push(`  ${routerUnknownLine(g.routerUnknown)}`);
  if (g.unplaced > 0) lines.push(`  ${unplacedLine(g.unplaced)}`);
  return [...lines, ...releasedLines(g)];
}

/**
 * The section in one clause, for the worker's log line, or null when there is
 * nothing to say. It names the most asked topic and leaves the rest to the
 * event row and the terminal.
 */
export function knowledgeGapSummary(g: KnowledgeGaps): string | null {
  const parts: string[] = [];
  const top = g.topics[0];
  if (top !== undefined) {
    parts.push(`${count(g.topicsTotal, "topic", "topics")} the shop could not answer (most asked: ${top.topic})`);
  }
  if (g.heldForHuman > 0) parts.push(heldForHumanLine(g.heldForHuman));
  if (g.routerUnknown > 0) parts.push(routerUnknownLine(g.routerUnknown));
  if (g.unplaced > 0) parts.push(unplacedLine(g.unplaced));
  // TA-1361: the released group, apart from the queue's figures, by its own name.
  const r = g.released;
  if (r !== undefined && r.topicsTotal > 0) parts.push(`${count(r.topicsTotal, "topic", "topics")} ${RELEASED_GAPS_TITLE}`);
  if (r !== undefined && r.unplaced > 0) parts.push(releasedUnplacedLine(r.unplaced));
  return parts.length === 0 ? null : parts.join("; ");
}
